import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map, catchError, of } from "rxjs";
import { environment } from "src/environments/environment";
import { SettingAdapter } from "../models/adapter/setting.adapter";
import { AgeAlertSetting } from "../models/age-alert-setting.model";
import { GmqAlertSetting } from "../models/gmq-alert-setting.model";
import { Group } from "../models/group.model";
import { ThresholdAlertSetting } from "../models/threshold-alert-setting.model";
import { ApiUrl } from "./api.url";

@Injectable({
    providedIn: 'root'
})
export class SettingsService {

    constructor(
        protected http: HttpClient,
        protected settingAdapter: SettingAdapter,
    ) { }

    public addAgeAlertGroup(age: number, groupId: number): Observable<AgeAlertSetting | null> {
        const body = {
            age,
            groupId
        };
        return this.http.post(`${environment.webappBaseUrl}${ApiUrl.POST_SETTING_AGE_GROUP}`, body).pipe(
            map((ageSetting: any) => {
                if (ageSetting) {
                    return this.settingAdapter.adaptAge(ageSetting);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public addAgeAlert(age: number, animalId: number): Observable<AgeAlertSetting | null> {
        const body = {
            age,
            animalId
        };
        return this.http.post(`${environment.webappBaseUrl}${ApiUrl.POST_SETTING_AGE}`, body).pipe(
            map((ageSetting: any) => {
                if (ageSetting) {
                    return this.settingAdapter.adaptAge(ageSetting);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public deleteAgeGroupAlert(ageId: number): Observable<boolean> {
        return this.http.delete(`${environment.webappBaseUrl}${ApiUrl.DELETE_SETTING_AGE_GROUP}`.replace(':id', ageId.toString())).pipe(
            map((deleted: any) => {
                return deleted;
            }),
            catchError(err => of(false)),
        );
    }

    public deleteAgeAlert(ageId: number): Observable<boolean> {
        return this.http.delete(`${environment.webappBaseUrl}${ApiUrl.DELETE_SETTING_AGE}`.replace(':id', ageId.toString())).pipe(
            map((deleted: any) => {
                return deleted;
            }),
            catchError(err => of(false)),
        );
    }

    public addGmqAlert(targetGmq: number, marginPercent: number, period: string, animalId: number): Observable<GmqAlertSetting | null> {
        const body = {
            targetGmq,
            marginPercent,
            period,
            animalId
        };
        return this.http.post(`${environment.webappBaseUrl}${ApiUrl.POST_SETTING_GMQ}`, body).pipe(
            map((setting: any) => {
                if (setting) {
                    return this.settingAdapter.adaptGmq(setting);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public addGmqAlertGroup(targetGmq: number, marginPercent: number, period: string, groupId: number): Observable<GmqAlertSetting | null> {
        const body = {
            targetGmq,
            marginPercent,
            period,
            groupId
        };
        return this.http.post(`${environment.webappBaseUrl}${ApiUrl.POST_SETTING_GMQ_GROUP}`, body).pipe(
            map((setting: any) => {
                if (setting) {
                    return this.settingAdapter.adaptGmq(setting);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public deleteGmqGroupAlert(gmqId: number): Observable<boolean> {
        return this.http.delete(`${environment.webappBaseUrl}${ApiUrl.DELETE_SETTING_GMQ_GROUP}`.replace(':id', gmqId.toString())).pipe(
            map((deleted: any) => {
                return deleted;
            }),
            catchError(err => of(false)),
        );
    }

    public deleteGmqAlert(gmqId: number): Observable<boolean> {
        return this.http.delete(`${environment.webappBaseUrl}${ApiUrl.DELETE_SETTING_GMQ}`.replace(':id', gmqId.toString())).pipe(
            map((deleted: any) => {
                return deleted;
            }),
            catchError(err => of(false)),
        );
    }

    public addThresholdGroupAlert(weight: number, groupId: number): Observable<ThresholdAlertSetting | null> {
        const body = {
            weight,
            groupId
        };
        return this.http.post(`${environment.webappBaseUrl}${ApiUrl.POST_SETTING_THRESHOLD_GROUP}`, body).pipe(
            map((setting: any) => {
                if (setting) {
                    return this.settingAdapter.adaptThreshold(setting);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public addThresholdAlert(weight: number, animalId: number): Observable<ThresholdAlertSetting | null> {
        const body = {
            weight,
            animalId
        };
        return this.http.post(`${environment.webappBaseUrl}${ApiUrl.POST_SETTING_THRESHOLD}`, body).pipe(
            map((setting: any) => {
                if (setting) {
                    return this.settingAdapter.adaptThreshold(setting);
                }
                return null;
            }),
            catchError(err => of(null)),
        );
    }

    public deleteThresholdGroupAlert(thresholdId: number): Observable<boolean> {
        return this.http.delete(`${environment.webappBaseUrl}${ApiUrl.DELETE_SETTING_THRESHOLD_GROUP}`.replace(':id', thresholdId.toString())).pipe(
            map((deleted: any) => {
                return deleted;
            }),
            catchError(err => of(false)),
        );
    }

    public deleteThresholdAlert(thresholdId: number): Observable<boolean> {
        return this.http.delete(`${environment.webappBaseUrl}${ApiUrl.DELETE_SETTING_THRESHOLD}`.replace(':id', thresholdId.toString())).pipe(
            map((deleted: any) => {
                return deleted;
            }),
            catchError(err => of(false)),
        );
    }
}
